/**
 * Shared chrome for the cookie banner (`CookieConsent`) and its preferences
 * sheet (`ManageCookiesModal`) — both open in the exact same footprint.
 *
 * Mobile: a full-bleed bottom slip (safe-area aware) with a soft upward
 * shadow, since no backdrop separates the banner from the page. From `md`:
 * a 420–460px card pinned bottom-left with a hairline edge.
 *
 * `blue-800` is the only brand blue that clears WCAG AA as text or as a fill
 * under white text (6.96:1) — see docs/claude/design-language.md → "blue-500
 * fails WCAG AA as text".
 */

/** Fixed positioning; callers add their own transition + visibility state. */
export const CONSENT_POSITION_CLASS =
  'fixed inset-x-0 bottom-0 z-9999 w-full ease-out md:right-auto md:bottom-6 md:left-6 md:w-105 lg:w-115'

/**
 * CSS custom property `CookieConsent` publishes on `<html>` while the banner
 * slip is on screen: its rendered height in px (cleared when it hides or
 * unmounts). Form CTAs in normal flow reserve it as bottom padding on mobile
 * with a static `pb-[var(--consent-h,0px)] md:pb-0` so the banner never sits
 * on top of them. Client-only — SSR markup never depends on it.
 */
export const CONSENT_HEIGHT_VAR = '--consent-h'

/**
 * Off-screen transform while hidden. `100%` alone leaves the top 24px of the
 * desktop card peeking above `bottom-6`; the extra 2rem clears it.
 */
export const CONSENT_HIDDEN_TRANSLATE = 'translate-y-[calc(100%+2rem)]'

/** The white sheet itself. Its first child is the blue brand rule. */
export const CONSENT_SHEET_CLASS =
  'bg-white-500 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-12px_32px_-16px_rgba(17,29,42,0.35)] md:border-x md:border-b md:border-ink-100/15 md:pb-0 md:shadow-[0_24px_48px_-24px_rgba(17,29,42,0.35)]'

/** Same 3px blue rule that tops the site footer. */
export const CONSENT_RULE_CLASS = 'h-0.75 bg-blue-500'

/** Fraunces title — the one display element on the sheet. */
export const CONSENT_TITLE_CLASS =
  'font-display leading-tight font-medium tracking-[-0.01em] text-ink-700'

/** Stacked on mobile (two labelled actions), one row from `md`. */
export const CONSENT_ACTIONS_CLASS =
  'flex flex-col gap-1 md:flex-row md:items-center md:gap-3'

export const CONSENT_PRIMARY_CLASS =
  'inline-flex min-h-11 w-full items-center justify-center rounded-full bg-blue-800 px-6 text-[15px] font-semibold text-white-500 transition-[background-color,transform] duration-200 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 md:w-auto md:flex-1'

export const CONSENT_SECONDARY_CLASS =
  'inline-flex min-h-11 w-full items-center justify-center rounded-full px-4 text-sm font-medium text-ink-700 underline decoration-ink-100/40 decoration-1 underline-offset-4 transition-colors duration-200 hover:decoration-ink-700 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-700 md:w-auto'
